import React, { useEffect, useState } from 'react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import Pagination from '../components/Pagination';
import Drawer from '../../sharedComponents/Drawer';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';
import { Eye, MoreVertical } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    revisedCopyByDeskService,
    categoryService,
    SubmissionRevisionRequest,
    Category,
    GetSubmissionRevisionByDeskPayload,
} from '../services/adminAuth';
import { formatDateDisplay } from '../../../utils/dateHelper';

const statusBadgeClasses = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('submit'))   return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s.includes('request'))  return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s.includes('approve'))  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s.includes('reject'))   return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
};

const leadAuthor = (row: SubmissionRevisionRequest) => {
    if (!row.bookAuthors || row.bookAuthors.length === 0) return '—';
    const lead = row.bookAuthors.find(a => a.isLeadAuthor);
    return (lead?.name ?? row.bookAuthors[0]?.name) ?? '—';
};

const AllRevisedSubmissions: React.FC = () => {
    const { showNotification } = useNotification();

    const [items, setItems] = useState<SubmissionRevisionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [filterCategory, setFilterCategory] = useState<number>(0);
    const [categories, setCategories] = useState<Category[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<SubmissionRevisionRequest | null>(null);

    // ── Fetchers ─────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await categoryService.getAllCategories(true);
                if (!cancelled && res.isSuccess && res.data) setCategories(res.data);
            } catch { /* fail soft */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const handler = setTimeout(async () => {
            setLoading(true);
            try {
                const payload: GetSubmissionRevisionByDeskPayload = {
                    pageNumber: currentPage,
                    pageSize,
                    sortBy: '',
                    sortDescending: true,
                    search,
                    categoryId: filterCategory,
                };
                const res = await revisedCopyByDeskService.getAllSubmissionRevision(payload);
                if (cancelled) return;
                if (res.isSuccess && res.data) {
                    setItems(res.data.data);
                    setTotalRecords(res.data.totalRecords);
                    setTotalPages(res.data.totalPages);
                } else {
                    showNotification('error', {
                        title: 'Error',
                        message: res.message || 'Failed to load revised submissions',
                    });
                }
            } catch {
                if (cancelled) return;
                showNotification('error', {
                    title: 'Error',
                    message: 'An error occurred while loading revised submissions',
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 350);

        return () => {
            cancelled = true;
            clearTimeout(handler);
        };
        // showNotification omitted on purpose — see same note in AllSubmissions.tsx
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, search, filterCategory]);

    // ── Actions ──────────────────────────────────────────────────────────────

    const handleViewDetails = (row: SubmissionRevisionRequest) => {
        setSelectedRow(row);
        setIsDetailsOpen(true);
    };

    // ── Columns ──────────────────────────────────────────────────────────────

    const columns: TableColumn<SubmissionRevisionRequest>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value || '—'}</span>,
        },
        {
            key: 'submissionTitle',
            label: 'Title',
            className: 'max-w-[280px]',
            render: (value: string) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value || '—'}
                </div>
            ),
        },
        {
            key: 'bookAuthors',
            label: 'Lead Author',
            sortValue: leadAuthor,
            render: (_, row) => <span className="text-sm text-gray-700">{leadAuthor(row)}</span>,
        },
        {
            key: 'status',
            label: 'Status',
            render: (value: string) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${statusBadgeClasses(value)}`}>
                    {value || '—'}
                </span>
            ),
        },
        {
            key: 'comment',
            label: 'Reviewer Comment',
            className: 'max-w-[260px]',
            render: (value: string) => (
                <span className="text-xs text-gray-500 italic block truncate" title={value}>
                    {value || '—'}
                </span>
            ),
        },
        {
            key: 'requestedAt',
            label: 'Requested',
            render: (value: string) => (
                <span className="text-xs text-gray-500">{value ? formatDateDisplay(value) : '—'}</span>
            ),
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            render: (value: string) => (
                <span className="text-xs text-gray-500">{value ? formatDateDisplay(value) : '—'}</span>
            ),
        },
    ];

    const actions: TableAction<SubmissionRevisionRequest>[] = [
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: () => undefined,
            title: 'Actions',
            variant: 'dropdown',
            dropdownItems: [
                {
                    label: 'View Details',
                    icon: <Eye className="w-4 h-4 text-blue-600" />,
                    onClick: handleViewDetails,
                    className: 'text-blue-600',
                },
            ],
        },
    ];

    // ── Filter Panel ─────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Thematic Area</label>
                    <select
                        value={filterCategory}
                        onChange={(e) => { setFilterCategory(Number(e.target.value)); setCurrentPage(1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                    >
                        <option value={0}>All thematic areas</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <button
                onClick={() => { setFilterCategory(0); setCurrentPage(1); }}
                className="mt-4 text-sm text-[#088A61] hover:underline font-medium"
            >
                Clear all filters
            </button>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Revised Copies by Author</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Every revised submission uploaded by an author across the system.
                </p>
            </div>

            <ReusableTable
                title="Revised Submissions"
                subTitle={`${totalRecords} record${totalRecords === 1 ? '' : 's'}`}
                columns={columns}
                data={items}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, ID, author…"
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No revised submissions found."
                showSearch
                showFilter
                filterPanel={filterPanel}
                isFilterOpen={isFilterOpen}
                onFilterToggle={() => setIsFilterOpen(o => !o)}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                title="Revised Submission Details"
                width="max-w-3xl"
            >
                {selectedRow && (
                    <AdminSubmissionDetails submissionId={selectedRow.submissionId} />
                )}
            </Drawer>
        </div>
    );
};

export default AllRevisedSubmissions;
